import type { Control } from "react-hook-form";
import { Select as ReactSelect } from "@/components/Selects/select";
import { Stethoscope, UserRound } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import type { AccountDetailsFormValues } from "@/features/onboarding/components/AccountDetailsOnboardingForm";
import {
  ONBOARDING_FORM_STYLES as styles,
  ONBOARDING_SELECT_CLASS_NAMES,
} from "@/features/onboarding/components/onboarding-form-styles";

const NAME_FIELDS = [
  {
    name: "firstName",
    label: "First name",
    placeholder: "Enter your first name",
    autoComplete: "given-name",
    optional: false,
  },
  {
    name: "lastName",
    label: "Last name",
    placeholder: "Enter your last name",
    autoComplete: "family-name",
    optional: false,
  },
  {
    name: "middleName",
    label: "Middle name",
    placeholder: "Enter your middle name",
    autoComplete: "additional-name",
    optional: true,
  },
  {
    name: "suffixName",
    label: "Suffix",
    placeholder: "Enter your suffix (e.g., Jr., Sr.)",
    autoComplete: "honorific-suffix",
    optional: true,
  },
] as const;

const PROVIDER_ROLE_INPUT_ID = "onboarding-provider-role";
const SPECIALTIES_INPUT_ID = "onboarding-specialties";

export function ProviderDetailsFields({
  control,
  healthcareRolesData,
  hasNoNpi,
}: {
  control: Control<AccountDetailsFormValues>;
  healthcareRolesData: { value: string; label: string }[];
  hasNoNpi: boolean;
}) {
  return (
    <div className="space-y-5">
      <section
        className={styles.section}
        aria-labelledby="onboarding-personal-heading"
      >
        <div className="mb-5 flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
            <UserRound className="size-5" aria-hidden="true" />
          </span>
          <div>
            <h3
              id="onboarding-personal-heading"
              className="font-semibold text-slate-900"
            >
              Personal information
            </h3>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Use your name as it appears on your professional records.
            </p>
          </div>
        </div>
        <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2">
          {NAME_FIELDS.map(
            ({ name, label, placeholder, autoComplete, optional }) => (
              <FormField
                key={name}
                name={name}
                control={control}
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className={styles.label}>
                      {label}{" "}
                      {optional ? (
                        <span className="font-normal text-slate-500">
                          (optional)
                        </span>
                      ) : (
                        <span className="text-destructive" aria-hidden="true">
                          *
                        </span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        aria-required={!optional}
                        className={styles.input}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ),
          )}
        </div>
      </section>

      <section
        className={styles.section}
        aria-labelledby="onboarding-professional-heading"
      >
        <div className="mb-5 flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
            <Stethoscope className="size-5" aria-hidden="true" />
          </span>
          <div>
            <h3
              id="onboarding-professional-heading"
              className="font-semibold text-slate-900"
            >
              Professional details
            </h3>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Tell us about your role and areas of practice.
            </p>
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField
            name="healthcareProfessionalType"
            control={control}
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel
                  htmlFor={PROVIDER_ROLE_INPUT_ID}
                  className={styles.label}
                >
                  Healthcare Provider Type{" "}
                  <span className="text-destructive" aria-hidden="true">
                    *
                  </span>
                </FormLabel>
                <FormControl>
                  <ReactSelect
                    inputId={PROVIDER_ROLE_INPUT_ID}
                    name={field.name}
                    ref={field.ref}
                    aria-required="true"
                    placeholder="Select Healthcare Provider Type"
                    options={healthcareRolesData}
                    onBlur={field.onBlur}
                    classNames={ONBOARDING_SELECT_CLASS_NAMES}
                    value={healthcareRolesData.find(
                      (option) => option.value === field.value,
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value ?? "")
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="specialtyCodes"
            control={control}
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel
                  htmlFor={SPECIALTIES_INPUT_ID}
                  className={styles.label}
                >
                  Specialty{" "}
                  <span className="text-destructive" aria-hidden="true">
                    *
                  </span>
                </FormLabel>
                <FormControl>
                  <SpecialtiesSelect
                    inputId={SPECIALTIES_INPUT_ID}
                    appearance="profile"
                    classNames={ONBOARDING_SELECT_CLASS_NAMES}
                    isMulti={true}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="npiNumber"
            control={control}
            render={({ field }) => {
              const hasNumbers = /\d/.test(field.value);
              return (
                <FormItem className="space-y-2 sm:col-span-2">
                  <FormLabel className={styles.label}>
                    NPI Number{" "}
                    {!hasNoNpi && (
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      maxLength={10}
                      inputMode="numeric"
                      type="text"
                      placeholder="Enter NPI Number"
                      disabled={hasNoNpi}
                      aria-required={!hasNoNpi}
                      className={styles.input}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="text-xs leading-5 text-slate-500">
                    Your 10-digit National Provider Identifier.
                  </FormDescription>
                  <FormMessage />
                  {!hasNumbers && (
                    <FormField
                      name="hasNoNpi"
                      control={control}
                      render={({ field: checkboxField }) => (
                        <FormItem className="flex min-h-11 items-center gap-3 space-y-0 rounded-xl bg-slate-50 px-3">
                          <FormControl>
                            <Checkbox
                              checked={checkboxField.value}
                              onCheckedChange={checkboxField.onChange}
                            />
                          </FormControl>
                          <FormLabel className="flex min-h-11 flex-1 cursor-pointer items-center text-sm font-normal text-slate-600">
                            I don&apos;t have an NPI
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  )}
                </FormItem>
              );
            }}
          />
        </div>
      </section>
    </div>
  );
}
